import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { quotationIntakeService } from '@features/quotation-intake/services';
import type { CustomerOption } from '@features/quotation-intake/types/quotationIntake.types';

interface CustomerComboboxProps {
  value: CustomerOption | { internalId: string; companyName: string } | null;
  onSelect: (customer: CustomerOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
}

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 1;

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '';
  return `₱${value.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function CustomerCombobox({
  value,
  onSelect,
  placeholder = 'Search customer by name or ID…',
  disabled,
  autoFocus,
  id,
}: CustomerComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const latestQueryRef = useRef('');

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    return value.companyName;
  }, [value]);

  useEffect(() => {
    const trimmed = query.trim();
    latestQueryRef.current = trimmed;

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsSearching(false);
      setError(null);
      return;
    }

    setIsSearching(true);
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const found = await quotationIntakeService.searchCustomers(trimmed, 20);
        if (latestQueryRef.current !== trimmed) return;
        setResults(found);
        setActiveIndex(0);
      } catch (err) {
        if (latestQueryRef.current !== trimmed) return;
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        if (latestQueryRef.current === trimmed) setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [query]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setQuery('');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [],
  );

  const commitSelection = useCallback(
    (opt: CustomerOption) => {
      onSelect(opt);
      setOpen(false);
      setQuery('');
    },
    [onSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = results[activeIndex];
      if (picked) commitSelection(picked);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  const showNoMatchBanner =
    !isSearching && !error && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          autoFocus={autoFocus}
          title={selectedLabel || undefined}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !value && 'text-muted-foreground',
          )}
        >
          <span className="truncate text-left">{selectedLabel || placeholder}</span>
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Clear customer"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(36rem,90vw)] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
          {isSearching && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {error && (
            <div className="px-3 py-3 text-sm text-destructive">{error}</div>
          )}
          {!error && results.length > 0 && (
            <ul role="listbox" className="py-1">
              {results.map((opt, idx) => {
                const isActive = idx === activeIndex;
                const isSelected = value?.internalId === opt.internalId;
                const contactLine = [opt.entityId, opt.phone, opt.email].filter(Boolean).join(' · ');
                const termsLine = [
                  opt.termsName ? `Terms: ${opt.termsName}` : null,
                  opt.creditLimit != null ? `Credit: ${formatCurrency(opt.creditLimit)}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <li key={opt.internalId} role="option" aria-selected={isActive}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => commitSelection(opt)}
                      className={cn(
                        'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm',
                        isActive && 'bg-accent',
                      )}
                    >
                      <div className="flex w-full items-center gap-2">
                        <span className="truncate font-medium">{opt.companyName}</span>
                        {opt.entityStatusName?.toLowerCase().includes('prospect') && (
                          <span className="rounded-sm bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning">
                            Prospect
                          </span>
                        )}
                        {isSelected && <Check className="ml-auto h-4 w-4 text-primary" />}
                      </div>
                      {contactLine && (
                        <span className="truncate text-xs text-muted-foreground">{contactLine}</span>
                      )}
                      {termsLine && (
                        <span className="truncate text-xs text-muted-foreground">{termsLine}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {showNoMatchBanner && (
            <div className="px-3 py-3 text-sm">
              <div className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                <div className="font-medium text-foreground">Customer not found</div>
                <p className="mt-0.5">
                  Please create the customer in NetSuite, then click{' '}
                  <span className="font-medium">Sync Master Data</span> to bring them in.
                </p>
              </div>
            </div>
          )}
          {!error && !isSearching && query.trim().length < MIN_QUERY_LENGTH && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              Type to search across {/* emphasize local cache */}<span className="font-medium">synced customers</span>.
            </div>
          )}
        </div>
        {value && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
            <span>
              Selected: <span className="font-medium text-foreground">{value.companyName}</span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => onSelect(null)}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
